import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { CONTACT_DETAILS } from "@/data/products";
import SEO from "@/components/SEO";
import ObfuscatedEmail from "@/components/ObfuscatedEmail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["PTT Radios", "Body Cameras", "IoT Hardware", "Multi-Network SIMs / eSIMs", "Mobile Data Plans", "Fixed Wireless Plans", "IoT Plans", "Business Backup Plans", "Data Pool Plans", "RV / Motorhome Connectivity", "Private APN / Secure VPN", "Android MDM", "BYOC Smart SIM", "Enterprise IoT", "Government / Public Safety", "Broadband / Fibre", "Reseller enquiry", "Custom Plan", "Other"];
const initialForm = { name: "", email: "", phone: "", company: "", interest: "", message: "", website: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [formLoadedAt] = useState(() => Date.now());
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const interest = searchParams.get("interest");
    const plan = searchParams.get("plan");
    if (interest || plan) {
      setForm((f) => ({
        ...f,
        interest: interest && INTERESTS.includes(interest) ? interest : f.interest,
        message: plan ? `I'm interested in: ${plan}` : f.message,
      }));
    }
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, form_loaded_at: formLoadedAt });
      toast.success("Message received — we'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || "Too many submissions. Please try again later.");
      } else {
        toast.error("Unable to send right now. Please call us on 03 360 3011.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        path="/contact"
        title="Contact PTT NZ · Christchurch, New Zealand"
        description="Get in touch with the PTT NZ team in Christchurch. Call 03 360 3011 or email us — NZ-based mission-critical connectivity support."
        keywords="contact PTT NZ, PTT NZ phone number, Christchurch telecommunications support, PTT NZ Australia enquiries, multi-network SIM quote New Zealand"
      />
      <Toaster position="top-right" richColors />
      <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-12">
          <div className="eyebrow">Contact</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] max-w-3xl">Let&apos;s get you on the air.</h1>
          <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">Whether you need ten PTT radios or a tri-network SIM in your own deployment, our Christchurch team is ready to help. We typically respond within one business day.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="bg-[var(--ptt-ink)] text-white p-7 border border-[var(--ptt-ink)]" data-testid="contact-details">
            <h2 className="font-display text-xl font-bold">Contact Us</h2>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 grid place-items-center bg-white/10 border border-white/15 shrink-0"><Phone size={16} className="text-[var(--ptt-teal)]" /></div>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Phone</div>
                  <a href={CONTACT_DETAILS.phoneHref} className="text-white hover:text-[var(--ptt-teal)] mt-0.5 inline-block" data-testid="contact-phone">{CONTACT_DETAILS.phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 grid place-items-center bg-white/10 border border-white/15 shrink-0"><Mail size={16} className="text-[var(--ptt-teal)]" /></div>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Email</div>
                  <ObfuscatedEmail
                    user={CONTACT_DETAILS.emailUser}
                    domain={CONTACT_DETAILS.emailDomain}
                    className="text-white hover:text-[var(--ptt-teal)] mt-0.5 text-sm"
                    buttonLabel="Click to reveal email"
                    testId="contact-email"
                  />
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 grid place-items-center bg-white/10 border border-white/15 shrink-0"><MapPin size={16} className="text-[var(--ptt-teal)]" /></div>
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Address</div>
                  <a href={CONTACT_DETAILS.mapsHref} target="_blank" rel="noreferrer" className="text-white hover:text-[var(--ptt-teal)] mt-0.5 inline-block" data-testid="contact-address">{CONTACT_DETAILS.address}</a>
                </div>
              </li>
            </ul>
          </div>
          <div className="mt-6 border border-[var(--ptt-line)] bg-white overflow-hidden aspect-[4/3]" data-testid="contact-map">
            <iframe
              title="PTT NZ location"
              src="https://www.google.com/maps?q=14+Hazeldean+Road+Addington+Christchurch+8024&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="bg-white border border-[var(--ptt-ink)] p-6 lg:p-8" data-testid="contact-form">
            <h2 className="font-display text-2xl font-bold">Send us a message</h2>
            <p className="text-sm text-[var(--ptt-muted)] mt-1">Tell us a little about your deployment or what you&apos;re looking for — we&apos;ll come back to you.</p>
            {/* Honeypot — hidden from real users, catches bots that auto-fill every field */}
            <div style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange}
                data-testid="contact-honeypot"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Name *" name="name" value={form.name} onChange={onChange} testId="contact-name" required />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testId="contact-email-input" required />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="contact-phone-input" />
              <Field label="Company" name="company" value={form.company} onChange={onChange} testId="contact-company" />
              <div className="sm:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">I&apos;m interested in</label>
                <select
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                  data-testid="contact-interest"
                  className="mt-1.5 w-full border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none px-3 py-2.5 text-sm bg-white"
                >
                  <option value="">Select an option…</option>
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-message"
                  rows={5}
                  required
                  className="mt-1.5 w-full border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none px-3 py-2.5 text-sm"
                  placeholder="How many users, where, and what are you trying to solve?"
                />
              </div>
            </div>
            <button type="submit" disabled={submitting} data-testid="contact-submit" className="ptt-button mt-6 px-6 py-3 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
              {submitting ? "Sending…" : <>Send message <Send size={14} /></>}
            </button>
            <p className="mt-4 text-[11px] font-mono-tech text-[var(--ptt-muted)] uppercase tracking-widest">We typically respond within 1 business day · NZ-based team</p>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testId, required }) => (
  <div>
    <label className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">{label}</label>
    <input
      data-testid={testId}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="mt-1.5 w-full border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none px-3 py-2.5 text-sm"
    />
  </div>
);

export default Contact;
